import { useEffect, useRef, type ReactNode } from "react";
import { FunnelSimpleIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Badge } from "@libs/zapi-ui/ds/components/badge";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from "@libs/zapi-ui/ds/components/popover";
import {
  SearchBar,
  SearchBarActions,
  SearchBarClear,
  SearchBarIcon,
  SearchBarInput
} from "@libs/zapi-ui/ds/components/search-bar";

function isTypingTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
  );
}

type TProps = {
  query: string;
  onQueryChange: (query: string) => void;
  activeFilters: number;
  children: ReactNode;
};

export function FilterSearch({ query, onQueryChange, activeFilters, children }: TProps) {
  const { t } = useTranslation("projects");
  const inputRef = useRef<HTMLInputElement>(null);
  const placeholder = t("toolbar.searchProjects");

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || isTypingTarget(event.target)) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <SearchBar data-slot="filter-search" className="flex-1">
      <SearchBarIcon />
      <SearchBarInput
        ref={inputRef}
        data-testid="projects-page-search"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && query) {
            event.preventDefault();
            onQueryChange("");
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        aria-keyshortcuts="/"
      />
      <SearchBarActions>
        {query && <SearchBarClear aria-label={t("toolbar.clearSearch")} onClick={() => onQueryChange("")} />}
        <Popover>
          <PopoverTrigger
            render={
              <Button
                variant={activeFilters > 0 ? "secondary" : "outline"}
                size="sm"
                shape="pill"
                data-testid="projects-filter-trigger"
                aria-label={activeFilters > 0 ? `${t("filters.heading")}, ${activeFilters}` : t("filters.heading")}
              />
            }
          >
            <FunnelSimpleIcon data-icon="inline-start" />
            {t("filters.heading")}
            {activeFilters > 0 && <Badge className="h-4 min-w-4 px-1 tabular-nums">{activeFilters}</Badge>}
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={10}
            className="w-[36rem] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden rounded-xl p-0"
          >
            <PopoverTitle className="sr-only">{t("filters.heading")}</PopoverTitle>
            {children}
          </PopoverContent>
        </Popover>
      </SearchBarActions>
    </SearchBar>
  );
}
